"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { UIButton } from "../buttons/ui-button"
import { UILabel } from "./ui-label"
import { UITypography } from "../display/ui-typography"
import {
  UIPopover,
  UIPopoverContent,
  UIPopoverTrigger,
} from "../layout/ui-popover"
import {
  UICommandBase,
  UICommandEmpty,
  UICommandGroup,
  UICommandInput,
  UICommandItem,
  UICommandList,
} from "./ui-command"
import { cn } from "../../lib/utils"
import { UIBox } from "../layout/ui-box"

// ============================================================================
// UICombobox Types
// ============================================================================

export interface UIComboboxOption {
  value: string
  label: string
  disabled?: boolean
}

export interface UIComboboxOptionGroup {
  label?: string
  options: UIComboboxOption[]
}

export interface UIComboboxProps {
  /**
   * 라벨
   */
  label?: string
  /**
   * 라벨 ID
   */
  labelId?: string
  /**
   * 설명 텍스트
   */
  description?: string
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 도움말 텍스트
   */
  helperText?: string
  /**
   * 필수 여부
   */
  required?: boolean
  /**
   * 플레이스홀더
   */
  placeholder?: string
  /**
   * 검색 플레이스홀더
   */
  searchPlaceholder?: string
  /**
   * 빈 결과 메시지
   */
  emptyMessage?: string
  /**
   * 컨테이너 클래스명
   */
  containerClassName?: string
  /**
   * 트리거 클래스명
   */
  triggerClassName?: string
  /**
   * 현재 선택된 값
   */
  value?: string
  /**
   * 값 변경 콜백
   */
  onValueChange?: (value: string) => void
  /**
   * 검색 콜백
   */
  onSearch?: (search: string) => void
  /**
   * 비활성화 여부
   */
  disabled?: boolean
  /**
   * 검색 가능 여부
   */
  searchable?: boolean
  /**
   * 옵션 목록 (단순한 형태)
   */
  options?: UIComboboxOption[]
  /**
   * 그룹화된 옵션 목록
   */
  groups?: UIComboboxOptionGroup[]
}

// ============================================================================
// UICombobox Component
// ============================================================================

export const UICombobox = React.forwardRef<
  HTMLButtonElement,
  UIComboboxProps
>(({
  label,
  labelId,
  description,
  error,
  helperText,
  required = false,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  containerClassName,
  triggerClassName,
  value,
  onValueChange,
  onSearch,
  disabled = false,
  searchable = true,
  options = [],
  groups = [],
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")

  const comboboxId = labelId || `combobox-${React.useId()}`

  // 모든 옵션을 통합 배열로 변환
  const allOptions = React.useMemo(() => {
    const flatOptions = [...options]
    groups.forEach(group => {
      flatOptions.push(...group.options)
    })
    return flatOptions
  }, [options, groups])

  // 선택된 옵션 찾기
  const selectedOption = allOptions.find(option => option.value === value)

  // 검색 필터링
  const filteredOptions = React.useMemo(() => {
    if (!searchable || !searchQuery) {
      return { options, groups }
    }

    const query = searchQuery.toLowerCase()

    const filteredSimpleOptions = options.filter(option =>
      option.label.toLowerCase().includes(query)
    )

    const filteredGroups = groups.map(group => ({
      ...group,
      options: group.options.filter(option =>
        option.label.toLowerCase().includes(query)
      )
    })).filter(group => group.options.length > 0)

    return {
      options: filteredSimpleOptions,
      groups: filteredGroups
    }
  }, [options, groups, searchQuery, searchable])

  const handleSelect = (selectedValue: string) => {
    if (onValueChange) {
      onValueChange(selectedValue === value ? "" : selectedValue)
    }
    setOpen(false)
  }

  const handleSearchChange = (search: string) => {
    setSearchQuery(search)
    if (onSearch) {
      onSearch(search)
    }
  }

  const renderOptions = () => {
    const { options: filteredSimpleOptions, groups: filteredGroupOptions } = filteredOptions

    return (
      <>
        {/* 단순 옵션들 */}
        {filteredSimpleOptions.map((option) => (
          <UICommandItem
            key={option.value}
            value={option.value}
            onSelect={handleSelect}
            disabled={option.disabled}
          >
            <Check
              className={cn(
                "mr-2 h-4 w-4",
                value === option.value ? "opacity-100" : "opacity-0"
              )}
            />
            {option.label}
          </UICommandItem>
        ))}

        {/* 그룹화된 옵션들 */}
        {filteredGroupOptions.map((group, groupIndex) => (
          <UICommandGroup key={groupIndex} heading={group.label}>
            {group.options.map((option) => (
              <UICommandItem
                key={option.value}
                value={option.value}
                onSelect={handleSelect}
                disabled={option.disabled}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </UICommandItem>
            ))}
          </UICommandGroup>
        ))}
      </>
    )
  }

  // 간단한 형태 (라벨, 설명, 에러 없음)
  if (!label && !description && !error && !helperText) {
    return (
      <UIPopover open={open} onOpenChange={setOpen}>
        <UIPopoverTrigger asChild>
          <UIButton
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full max-w-full min-w-0 flex-shrink-0 justify-between",
              triggerClassName
            )}
            disabled={disabled}
          >
            {selectedOption ? selectedOption.label : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </UIButton>
        </UIPopoverTrigger>
        <UIPopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
          <UICommandBase>
            {searchable && (
              <UICommandInput
                placeholder={searchPlaceholder}
                value={searchQuery}
                onValueChange={handleSearchChange}
              />
            )}
            <UICommandList>
              <UICommandEmpty>{emptyMessage}</UICommandEmpty>
              {renderOptions()}
            </UICommandList>
          </UICommandBase>
        </UIPopoverContent>
      </UIPopover>
    )
  }

  // 완전한 형태 (라벨, 설명, 에러 포함)
  return (
    <UIBox className={cn("w-full max-w-full min-w-0 flex-shrink-0 space-y-2", containerClassName)}>
      {label && (
        <UILabel
          htmlFor={comboboxId}
          className={cn(
            "text-sm font-medium leading-none",
            required && "after:content-['*'] after:ml-0.5 after:text-destructive",
            error && "text-destructive"
          )}
        >
          {label}
        </UILabel>
      )}

      {description && (
        <UITypography variant="caption" color="muted">{description}</UITypography>
      )}

      <UIPopover open={open} onOpenChange={setOpen}>
        <UIPopoverTrigger asChild>
          <UIButton
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            id={comboboxId}
            className={cn(
              "w-full justify-between",
              error && "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20",
              triggerClassName
            )}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={
              error ? `${comboboxId}-error` :
              helperText ? `${comboboxId}-helper` :
              undefined
            }
            disabled={disabled}
          >
            {selectedOption ? selectedOption.label : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </UIButton>
        </UIPopoverTrigger>
        <UIPopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
          <UICommandBase>
            {searchable && (
              <UICommandInput
                placeholder={searchPlaceholder}
                value={searchQuery}
                onValueChange={handleSearchChange}
              />
            )}
            <UICommandList>
              <UICommandEmpty>{emptyMessage}</UICommandEmpty>
              {renderOptions()}
            </UICommandList>
          </UICommandBase>
        </UIPopoverContent>
      </UIPopover>

      {error && (
        <UITypography
          as="p"
          id={`${comboboxId}-error`}
          variant="caption"
          color="destructive"
          role="alert"
        >
          {error}
        </UITypography>
      )}

      {helperText && !error && (
        <UITypography
          as="p"
          id={`${comboboxId}-helper`}
          variant="caption"
          color="muted"
        >
          {helperText}
        </UITypography>
      )}
    </UIBox>
  )
})

UICombobox.displayName = "UICombobox"

// ============================================================================
// Preset Combobox Components
// ============================================================================

export interface UIComboboxSimpleProps extends Omit<UIComboboxProps, 'groups'> {
  options: UIComboboxOption[]
}

export const UIComboboxSimple = React.forwardRef<
  HTMLButtonElement,
  UIComboboxSimpleProps
>((props, ref) => {
  return <UICombobox ref={ref} {...props} />
})

UIComboboxSimple.displayName = "UIComboboxSimple"

// ============================================================================
// Type Re-exports
// ============================================================================

